import type { CSSProperties, JSX, ReactNode } from 'react';

export function TabBar<T extends string>({
  label,
  tab,
  items,
  onTab,
  mark,
  badge,
  addon,
}: {
  label: string;
  tab: T | null;
  items: readonly {
    id: T;
    label: string;
    icon?: ReactNode;
    disabled?: boolean;
    popup?: boolean;
  }[];
  onTab: (tab: T) => void;
  mark?: (id: T) => 'dirty' | 'live' | null;
  /** Count shown on the icon. `label` spells it out for screen readers. */
  badge?: (id: T) => { count: number; label: string } | null;
  /** Overlay parked on a tab cell (e.g. a popup menu). */
  addon?: (id: T) => ReactNode;
}): JSX.Element {
  return (
    <nav
      className="session-tabs"
      role="tablist"
      aria-label={label}
      style={{ '--tab-n': items.length } as CSSProperties}
    >
      {items.map((item) => {
        const selected = tab === item.id;
        const dot = mark?.(item.id) ?? null;
        const count = badge?.(item.id) ?? null;
        return (
          <span key={item.id} className="session-tab-cell">
            {addon?.(item.id)}
            <button
              type="button"
              role="tab"
              id={`tab-${item.id}`}
              aria-selected={selected}
              aria-controls={item.popup ? undefined : `pane-${item.id}`}
              aria-haspopup={item.popup ? 'true' : undefined}
              aria-expanded={item.popup ? selected : undefined}
              aria-label={count ? `${item.label}, ${count.label}` : undefined}
              className={`session-tab${selected ? ' session-tab-on' : ''}`}
              disabled={item.disabled}
              onClick={() => onTab(item.id)}
            >
              <span className="session-tab-icon">
                {item.icon}
                {dot && <span className={`session-tab-dot session-tab-dot-${dot}`} aria-hidden="true" />}
                {count && (
                  <span className="session-tab-badge" aria-hidden="true">
                    {count.count > 99 ? '99+' : count.count}
                  </span>
                )}
              </span>
              <span className="session-tab-label">{item.label}</span>
            </button>
          </span>
        );
      })}
    </nav>
  );
}
